import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Chip,
    IconButton,
    Divider,
    Avatar,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Save as SaveIcon,
    Group as GroupIcon,
    Palette as PaletteIcon,
    Person as PersonIcon,
    Add as AddIcon
} from '@mui/icons-material';

const mockClientGroups = [
    {
        id: 1,
        name: 'Entreprises Premium',
        description: 'Grandes entreprises avec abonnements premium',
        clientsCount: 45,
        color: '#10b981',
        totalRevenue: 15650,
        averageSubscription: 'Premium'
    },
    {
        id: 2,
        name: 'Particuliers',
        description: 'Clients particuliers individuels',
        clientsCount: 127,
        color: '#3b82f6',
        totalRevenue: 3850,
        averageSubscription: 'Basic'
    }
];

const mockClients = [
    { id: 1, name: 'Jean Dupont', email: 'jean@email.com', subscription: 'Premium' },
    { id: 2, name: 'Marie Martin', email: 'marie@email.com', subscription: 'Basic' },
    { id: 3, name: 'Tech Corp', email: 'contact@techcorp.com', subscription: 'Enterprise' }
];

const availableColors = [
    { name: 'Bleu', value: '#3b82f6' },
    { name: 'Vert', value: '#10b981' },
    { name: 'Orange', value: '#f59e0b' },
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Rouge', value: '#ef4444' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Rose', value: '#ec4899' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Emeraude', value: '#059669' },
    { name: 'Amber', value: '#d97706' }
];

const subscriptionTypes = [
    'Basic',
    'Standard',
    'Premium',
    'Enterprise',
    'Test',
    'Interne',
    'Startup'
];

const ClientGroupFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = id && id !== 'new';

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        color: '#3b82f6',
        averageSubscription: 'Basic'
    });

    const [assignedClients, setAssignedClients] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isEditing) {
            const group = mockClientGroups.find(g => g.id === parseInt(id));
            if (group) {
                setFormData({
                    name: group.name,
                    description: group.description,
                    color: group.color,
                    averageSubscription: group.averageSubscription
                });
                // Simulation des clients assignés
                setAssignedClients(mockClients.slice(0, 2));
            }
        }
    }, [id, isEditing]);

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Le nom du groupe est requis';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'La description est requise';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);

        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            console.log('Sauvegarde du groupe:', formData);
            console.log('Clients assignés:', assignedClients);
            navigate('/client-groups');
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/client-groups');
    };

    const handleAddClient = () => {
        // Simulation d'ajout de client
        const availableClients = mockClients.filter(
            client => !assignedClients.find(assigned => assigned.id === client.id)
        );

        if (availableClients.length > 0) {
            setAssignedClients(prev => [...prev, availableClients[0]]);
        }
    };

    const handleRemoveClient = (clientId) => {
        setAssignedClients(prev => prev.filter(client => client.id !== clientId));
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <GroupIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {isEditing ? 'Modifier le groupe de clients' : 'Nouveau groupe de clients'}
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 3, color: 'text.primary' }}>
                                Informations générales
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Nom du groupe"
                                        value={formData.name}
                                        onChange={(e) => handleChange('name', e.target.value)}
                                        error={!!errors.name}
                                        helperText={errors.name}
                                        placeholder="Ex: Entreprises Premium"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        value={formData.description}
                                        onChange={(e) => handleChange('description', e.target.value)}
                                        error={!!errors.description}
                                        helperText={errors.description}
                                        placeholder="Description du groupe de clients"
                                        multiline
                                        rows={3}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Couleur</InputLabel>
                                        <Select
                                            value={formData.color}
                                            onChange={(e) => handleChange('color', e.target.value)}
                                            label="Couleur"
                                        >
                                            {availableColors.map((color) => (
                                                <MenuItem key={color.value} value={color.value}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                bgcolor: color.value,
                                                                borderRadius: '50%',
                                                                mr: 2
                                                            }}
                                                        />
                                                        {color.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Type d'abonnement principal</InputLabel>
                                        <Select
                                            value={formData.averageSubscription}
                                            onChange={(e) => handleChange('averageSubscription', e.target.value)}
                                            label="Type d'abonnement principal"
                                        >
                                            {subscriptionTypes.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                                    Clients assignés ({assignedClients.length})
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddClient}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                >
                                    Ajouter un client
                                </Button>
                            </Box>

                            {assignedClients.length > 0 ? (
                                <List>
                                    {assignedClients.map((client) => (
                                        <ListItem key={client.id} sx={{ px: 0 }}>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: formData.color }}>
                                                    <PersonIcon />
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={client.name}
                                                secondary={`${client.email} • ${client.subscription}`}
                                            />
                                            <ListItemSecondaryAction>
                                                <Button
                                                    size="small"
                                                    onClick={() => handleRemoveClient(client.id)}
                                                    sx={{ color: 'error.main' }}
                                                >
                                                    Retirer
                                                </Button>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Aucun client assigné pour le moment
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Aperçu
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar sx={{ bgcolor: formData.color, mr: 2 }}>
                                    <GroupIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {formData.name || 'Nom du groupe'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {assignedClients.length} client(s)
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                {formData.description || 'Description du groupe'}
                            </Typography>

                            <Chip
                                label={formData.averageSubscription}
                                sx={{
                                    bgcolor: formData.color,
                                    color: 'white',
                                    fontWeight: 600,
                                    mb: 2
                                }}
                            />

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PaletteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Couleur sélectionnée
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: formData.color,
                                    borderRadius: 1,
                                    border: '2px solid',
                                    borderColor: 'divider'
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
                                Actions
                            </Typography>

                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                disabled={loading}
                                sx={{ mb: 2 }}
                            >
                                {loading ? 'Sauvegarde...' : (isEditing ? 'Mettre à jour' : 'Créer le groupe')}
                            </Button>

                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleBack}
                                sx={{
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    '&:hover': {
                                        borderColor: 'text.secondary',
                                        bgcolor: 'action.hover'
                                    }
                                }}
                            >
                                Annuler
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    Veuillez corriger les erreurs dans le formulaire.
                </Alert>
            )}
        </Box>
    );
};

export default ClientGroupFormPage;